/**
 * Helper types for message-type and -position
 */
type FeedbackType = "success" | "error" | "info" | "warning";
type FeedbackPosition = "top" | "left" | "bottom" | "right";

/**
 * Class to implement the feedback functionality,
 * which can be used by any other code to build upon
 */
export class Feedback {
    /**
     * The default duration (in milliseconds)
     * that a message is displayed
     */
    private static readonly DEFAULT_DURATION: number = 3000;

    /**
     * The offset (in pixels) from the target
     * element where the message appears
     */
    private static readonly OFFSET: number = 8;

    /**
     * The minimum padding (in pixels) between the
     * message and the edge of the viewport
     */
    private static readonly VIEWPORT_PADDING: number = 12;

    /**
     * The spacing (in pixels) between stacked messages
     */
    private static readonly STACK_SPACING: number = 3;

    /**
     * A container that wraps all messages
     */
    private static container: HTMLDivElement | null = null;

    /**
     * Map to track active messages per element
     */
    private static activeMessages: Map<Element, HTMLDivElement[]> = new Map();

    /**
     * Flag to track the initialization of triggers
     * for repositioning on scroll and resize events
     */
    private static observerInitialized: boolean = false;

    /**
     * Method to flash a feedback message
     *
     * @param {Element} target
     * @param {FeedbackType} type
     * @param {string} text
     * @param {number} [duration=Feedback.DEFAULT_DURATION]
     * @returns {void}
     */
    public static flash(
        target: Element,
        type: FeedbackType,
        text: string,
        duration: number = Feedback.DEFAULT_DURATION,
    ): void {
        Feedback.watchRepositionEvents();

        // Create message element
        const message = document.createElement("div");
        message.setAttribute("feedback", type);
        message.textContent = text;

        // Add the message to the container
        Feedback.register(message);

        // Stacking logic
        if (!this.activeMessages.has(target)) {
            this.activeMessages.set(target, []);
        }
        const stack = this.activeMessages.get(target)!;
        stack.push(message);

        // Initial positioning
        Feedback.positionMessages(target);

        // Fade in
        requestAnimationFrame(() => {
            message.style.opacity = "1";
        });

        // Remove after duration
        setTimeout(() => {
            message.style.opacity = "0";

            setTimeout(() => {
                message.remove();
                const index = stack.indexOf(message);
                if (index > -1) stack.splice(index, 1);
                if (stack.length === 0) this.activeMessages.delete(target);
                Feedback.positionMessages(target);
            }, 300);
        }, duration);
    }

    /**
     * Method to append a message to the container; creates
     * a container for the messages if none exists
     *
     * @param {HTMLDivElement} message
     * @returns {void}
     */
    private static register(message: HTMLDivElement): void {
        if (!Feedback.container) {
            Feedback.container = document.createElement("div");
            Feedback.container.setAttribute("feedback", "container");
            document.body.appendChild(Feedback.container);
        }
        Feedback.container.appendChild(message);
    }

    /**
     * Method to attach scroll and resize listeners once in order
     * to reposition any messages when those events are fired
     *
     * @returns {void}
     */
    private static watchRepositionEvents(): void {
        if (!Feedback.observerInitialized) {
            window.addEventListener("scroll", Feedback.reposition, true);
            window.addEventListener("resize", Feedback.reposition, true);
            Feedback.observerInitialized = true;
        }
    }

    /**
     * Repositions all messages for all targets
     *
     * @returns {void}
     */
    private static reposition(): void {
        Feedback.activeMessages.forEach((_stack, target) => {
            Feedback.positionMessages(target);
        });
    }

    /**
     * Method to position all messages for a given
     * target; stacking them to keep it tidy
     *
     * @param {Element} target
     * @returns {void}
     */
    private static positionMessages(target: Element): void {
        const stack = Feedback.activeMessages.get(target) ?? [];
        if (!stack || stack.length === 0) return;

        const rect = target.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const padding = Feedback.VIEWPORT_PADDING;
        const offset = Feedback.OFFSET;

        // Try positions in order: top, left, bottom, right
        // For each, check if the stack fits in the viewport with padding
        let messagesRects: DOMRect[] = [];
        let position: FeedbackPosition = "top";
        let fits = false;

        // Pre-measure all message heights and widths
        messagesRects = stack.map((message) => message.getBoundingClientRect());

        // Helper to check the fit
        function checkFit(position: FeedbackPosition): boolean {
            let fits = true;
            for (let i = 0; i < stack.length; ++i) {
                const messageRect = messagesRects[i];
                let top = 0,
                    left = 0;

                switch (position) {
                    case "top":
                        top =
                            rect.top -
                            offset -
                            (i + 1) * messageRect.height -
                            i * Feedback.STACK_SPACING;
                        left = rect.left + (rect.width - messageRect.width) / 2;
                        break;
                    case "left":
                        top =
                            rect.top +
                            (rect.height - messageRect.height) / 2 +
                            i * (messageRect.height + Feedback.STACK_SPACING);
                        left = rect.left - offset - messageRect.width;
                        break;
                    case "bottom":
                        top =
                            rect.bottom +
                            offset +
                            i * (messageRect.height + Feedback.STACK_SPACING);
                        left = rect.left + (rect.width - messageRect.width) / 2;
                        break;
                    case "right":
                        top =
                            rect.top +
                            (rect.height - messageRect.height) / 2 +
                            i * (messageRect.height + Feedback.STACK_SPACING);
                        left = rect.right + offset;
                        break;
                }

                // Clamp to padding
                if (
                    left < padding ||
                    left + messageRect.width > viewportWidth - padding ||
                    top < padding ||
                    top + messageRect.height > viewportHeight - padding
                ) {
                    fits = false;
                    break;
                }
            }
            return fits;
        }

        // Try each position
        for (const _position of ["top", "left", "bottom", "right"] as const) {
            if (checkFit(_position)) {
                position = _position;
                fits = true;
                break;
            }
        }

        // Now position each message
        for (let i = 0; i < stack.length; ++i) {
            const message = stack[i];
            const messageRect = messagesRects[i];
            let top = 0,
                left = 0;
            switch (position) {
                case "top":
                    top =
                        rect.top -
                        offset -
                        (i + 1) * messageRect.height -
                        i * Feedback.STACK_SPACING;
                    left = rect.left + (rect.width - messageRect.width) / 2;
                    break;
                case "left":
                    top =
                        rect.top +
                        (rect.height - messageRect.height) / 2 +
                        i * (messageRect.height + Feedback.STACK_SPACING);
                    left = rect.left - offset - messageRect.width;
                    break;
                case "bottom":
                    top =
                        rect.bottom +
                        offset +
                        i * (messageRect.height + Feedback.STACK_SPACING);
                    left = rect.left + (rect.width - messageRect.width) / 2;
                    break;
                case "right":
                    top =
                        rect.top +
                        (rect.height - messageRect.height) / 2 +
                        i * (messageRect.height + Feedback.STACK_SPACING);
                    left = rect.right + offset;
                    break;
            }

            // Clamp to the viewport with padding
            left = Math.max(
                padding,
                Math.min(left, viewportWidth - messageRect.width - padding),
            );
            top = Math.max(
                padding,
                Math.min(top, viewportHeight - messageRect.height - padding),
            );
            message.style.left = `${left}px`;
            message.style.top = `${top}px`;
        }
    }
}
